import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CalendarCheck, CalendarClock, CalendarPlus, Play, Settings2 } from "lucide-react";
import ScheduleSettingsDialog from "./ScheduleSettingsDialog";
import ScheduleVisitDialog from "./ScheduleVisitDialog";
import { fmtDate } from "./assessmentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIND_LABEL = { supervisory: "Supervisory visit", readmission: "Re-admission", form_update: "Plan of Care update" };
const SOURCE_LABEL = { supervisory: "last supervisory visit", legacy_supervisory: "last supervisory visit (log)", admission: "admission/re-admission", client_start: "client start" };

export function dueText(item) {
  const n = Math.abs(item.days_left);
  if (item.status === "overdue") return `${n} day${n === 1 ? "" : "s"} past due`;
  if (item.days_left === 0) return "due today";
  return `due in ${n} day${n === 1 ? "" : "s"}`;
}

export function DueBadge({ item }) {
  if (item.status === "overdue") return <Badge className="bg-red-100 text-red-800 border-red-200 gap-1 text-[10px]" data-testid={`due-badge-${item.kind}`}><AlertTriangle className="w-3 h-3" /> {dueText(item)}</Badge>;
  if (item.status === "due_soon") return <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1 text-[10px]" data-testid={`due-badge-${item.kind}`}><CalendarClock className="w-3 h-3" /> {dueText(item)}</Badge>;
  return <Badge variant="outline" className="text-[10px] text-slate-600" data-testid={`due-badge-${item.kind}`}>{dueText(item)}</Badge>;
}

export function ScheduledInfo({ item }) {
  const s = item.scheduled_appointment;
  if (!s) return null;
  return (
    <Link to="/schedule" className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-800 hover:bg-sky-100" data-testid={`scheduled-info-${item.kind}`}>
      <CalendarCheck className="w-3 h-3" /> Scheduled for {fmtDate(s.scheduled_date)}{s.scheduled_time ? ` ${s.scheduled_time}` : ""}{s.employee_name ? ` with ${s.employee_name}` : ""}
    </Link>
  );
}

export default function DuePanel({ canEdit, isAdmin, onStart, refreshKey }) {
  const [data, setData] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scheduling, setScheduling] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/assessments/due`).then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} }));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data) return null;
  const items = data.items || [];
  const s = data.settings || {};

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="due-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-rose-600" /> Visits coming due
            {data.counts?.overdue > 0 && <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" data-testid="due-count-overdue">{data.counts.overdue} past due</Badge>}
            {data.counts?.due_soon > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="due-count-soon">{data.counts.due_soon} due soon</Badge>}
          </h2>
          <p className="text-xs text-slate-500">Supervisory every {s.supervisory_interval_days} days · re-admission every {s.readmission_default_days} days{s.readmission_by_insurance?.length ? ` (${s.readmission_by_insurance.length} insurance override${s.readmission_by_insurance.length === 1 ? "" : "s"})` : ""} · warning {s.due_soon_days} days ahead.</p>
        </div>
        {isAdmin && (
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setSettingsOpen(true)} data-testid="due-settings-btn">
            <Settings2 className="w-3.5 h-3.5" /> Schedule
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-500 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2" data-testid="due-empty">All active clients are current — nothing due in the next {s.due_soon_days} days.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((it) => (
            <div key={`${it.kind}-${it.client_id}`} className="flex flex-wrap items-center gap-3 py-2" data-testid={`due-row-${it.kind}-${it.client_id}`}>
              <div className="flex-1 min-w-[220px]">
                <p className="text-sm font-medium text-slate-900">
                  <Link to={`/clients/${it.client_id}?tab=assessments`} className="hover:underline">{it.client_name}</Link>
                  <span className="text-slate-500 font-normal"> · {it.kind === "form_update" ? it.label : KIND_LABEL[it.kind]}</span>
                </p>
                <p className="text-xs text-slate-500">
                  Due {fmtDate(it.due_date)} · {SOURCE_LABEL[it.last_source] || "last visit"} {fmtDate(it.last_date)}
                  {it.kind === "readmission" && it.insurance_company ? ` · ${it.insurance_company} (${it.interval_days}d)` : ""}
                </p>
              </div>
              <DueBadge item={it} />
              <ScheduledInfo item={it} />
              {canEdit && (
                <>
                  {!it.scheduled_appointment && (
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => setScheduling(it)} data-testid={`due-schedule-${it.kind}-${it.client_id}`}>
                      <CalendarPlus className="w-3.5 h-3.5" /> Schedule visit
                    </Button>
                  )}
                  <Button size="sm" className="gap-1 bg-rose-600 hover:bg-rose-700" onClick={() => onStart(it)} data-testid={`due-start-${it.kind}-${it.client_id}`}>
                    <Play className="w-3.5 h-3.5" /> Start {it.kind === "supervisory" ? "supervisory visit" : it.kind === "form_update" ? "visit & update form" : "re-admission"}
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <ScheduleSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={load} />
      <ScheduleVisitDialog item={scheduling} onClose={() => setScheduling(null)} onScheduled={load} />
    </div>
  );
}
